'use client'

import type { Editor } from '@tiptap/react'
import type * as React from 'react'

import { TooltipProvider } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

import { EditorCharacterCount } from './editor-character-count.atom'
import { EditorContentArea } from './editor-content-area.atom'
import type { ToolbarConfig } from './markdown-editor.interface'
import { EditorToolbar } from './toolbar/editor-toolbar.molecule'

const wrapperClasses = cn(
  'markdown-editor overflow-hidden rounded-lg border border-input bg-background',
  'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background'
)

export interface EditorContainerProps {
  editor: Editor | null
  toolbar: ToolbarConfig
  isDisabled: boolean
  minHeight: string
  maxHeight: string
  characterCount: number
  maxLength?: number
  isShowCharCount: boolean
  className?: string
}

/**
 * Container component that renders the editor with toolbar and character count
 */
export const EditorContainer: React.FC<EditorContainerProps> = ({
  editor,
  toolbar,
  isDisabled,
  minHeight,
  maxHeight,
  characterCount,
  maxLength,
  isShowCharCount,
  className,
}) => {
  const isShowToolbar = toolbar !== 'none' && toolbar !== 'floating'

  return (
    <TooltipProvider delayDuration={300}>
      <div className={cn(wrapperClasses, isDisabled && 'cursor-not-allowed opacity-50', className)}>
        {isShowToolbar && <EditorToolbar editor={editor} config={toolbar} disabled={isDisabled} />}

        <EditorContentArea editor={editor} minHeight={minHeight} maxHeight={maxHeight} />

        {isShowCharCount && (
          <EditorCharacterCount characterCount={characterCount} maxLength={maxLength} />
        )}
      </div>
    </TooltipProvider>
  )
}

EditorContainer.displayName = 'EditorContainer'
